/**
 * Hooks de administración del catálogo de apps.
 *
 * Las mutaciones envían CSRF automáticamente (lo añade `api/client`) e
 * invalidan las queries afectadas tras éxito.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/api/client';
import type { AppEntry } from '@/apps/useApps';

export interface AppAdminEntry extends AppEntry {
  soft_deleted_at: string | null;
}

export interface AppPermissionEntry {
  id: number;
  app_id: number;
  entra_group_id: string;
}

export interface CreateAppPayload {
  slug: string;
  name: string;
  url: string;
  description?: string | null;
  icon_url?: string | null;
  enabled?: boolean;
}

const ADMIN_APPS_KEY = ['admin', 'apps'] as const;

export function useAdminApps(includeDeleted = false) {
  return useQuery<AppAdminEntry[], ApiError>({
    queryKey: [...ADMIN_APPS_KEY, { includeDeleted }],
    queryFn: () =>
      api<AppAdminEntry[]>(
        `/api/admin/apps${includeDeleted ? '?include_deleted=true' : ''}`,
      ),
  });
}

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation<AppAdminEntry, ApiError, CreateAppPayload>({
    mutationFn: (payload) =>
      api<AppAdminEntry>('/api/admin/apps', { method: 'POST', body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_APPS_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

export interface UpdateAppPayload {
  name?: string;
  url?: string;
  icon_url?: string | null;
  description?: string | null;
  category?: string | null;
  subcategory?: string | null;
  is_public?: boolean;
  enabled?: boolean;
}

/** Actualiza una app (PUT parcial). Usado, p. ej., para el toggle "Pública". */
export function useUpdateApp() {
  const qc = useQueryClient();
  return useMutation<AppAdminEntry, ApiError, { id: number; body: UpdateAppPayload }>({
    mutationFn: ({ id, body }) =>
      api<AppAdminEntry>(`/api/admin/apps/${id}`, { method: 'PUT', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_APPS_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

export function useSoftDeleteApp() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (appId) =>
      api<void>(`/api/admin/apps/${appId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_APPS_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

export function useAppPermissions(appId: number | null) {
  return useQuery<AppPermissionEntry[], ApiError>({
    queryKey: ['admin', 'apps', appId, 'permissions'],
    queryFn: () => api<AppPermissionEntry[]>(`/api/admin/apps/${appId}/permissions`),
    enabled: appId !== null,
  });
}

export function useAddPermission(appId: number) {
  const qc = useQueryClient();
  return useMutation<AppPermissionEntry, ApiError, string>({
    mutationFn: (entra_group_id) =>
      api<AppPermissionEntry>(`/api/admin/apps/${appId}/permissions`, {
        method: 'POST',
        body: { entra_group_id },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'apps', appId, 'permissions'] });
    },
  });
}

export function useRemovePermission(appId: number) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (permissionId) =>
      api<void>(`/api/admin/apps/${appId}/permissions/${permissionId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'apps', appId, 'permissions'] });
    },
  });
}
